import React, { useState, useEffect } from "react";
import "./CMS.css";
import useCmsStore from "../../services/useCmsStore";
import usePaymentGuard from "../../hooks/usePaymentGuard";
import PaymentBlockModal from "../PaymentBlockModal/PaymentBlockModal";
import FullScreenLoader from "../FullScreenLoader/FullScreenLoader";
import CategoryModal from "./components/CategoryModal";
import TagModal from "./components/TagModal";
import AuthorModal from "./components/AuthorModal";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import {
  FiFileText,
  FiEdit3,
  FiFolder,
  FiTag,
  FiUsers,
  FiImage,
  FiArrowRight,
  FiPlus,
  FiSearch,
  FiFilter,
  FiMoreHorizontal,
  FiTrash2,
  FiCopy,
  FiEye,
  FiEdit,
  FiExternalLink,
  FiRefreshCw,
  FiCheck,
  FiClock,
  FiX,
} from "react-icons/fi";

const CMS = () => {
  const navigate = useNavigate();
  const {
    pages,
    posts,
    categories,
    tags,
    authors,
    pagesLoading,
    postsLoading,
    categoriesLoading,
    tagsLoading,
    authorsLoading,
    pagesPagination,
    postsPagination,
    error,
    fetchPages,
    fetchPosts,
    fetchCategories,
    fetchTags,
    fetchAuthors,
    deletePage,
    deletePost,
    deleteCategory,
    deleteTag,
    deleteAuthor,
    publishPage,
    publishPost,
    duplicatePage,
    clearError,
  } = useCmsStore();

  const {
    needsPayment,
    showBlockModal,
    closeBlockModal,
    showPaymentRequired,
  } = usePaymentGuard();

  const [activeTab, setActiveTab] = useState("pages");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showAuthorModal, setShowAuthorModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingTag, setEditingTag] = useState(null);
  const [editingAuthor, setEditingAuthor] = useState(null);

  useEffect(() => {
    if (!loadedOnce) {
      fetchPages();
      fetchPosts();
      fetchCategories();
      fetchTags();
      fetchAuthors();
      setLoadedOnce(true);
    }
  }, [loadedOnce, fetchPages, fetchPosts, fetchCategories, fetchTags, fetchAuthors]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, clearError]);

  const handleCreateNew = () => {
    if (needsPayment && showPaymentRequired()) return;

    switch (activeTab) {
      case "pages":
        navigate("/cms/pages/new");
        break;
      case "posts":
        navigate("/cms/posts/new");
        break;
      case "categories":
        setEditingCategory(null);
        setShowCategoryModal(true);
        break;
      case "tags":
        setEditingTag(null);
        setShowTagModal(true);
        break;
      case "authors":
        setEditingAuthor(null);
        setShowAuthorModal(true);
        break;
      default:
        break;
    }
  };

  const handleEditItem = (item) => {
    if (needsPayment && showPaymentRequired()) return;

    switch (activeTab) {
      case "pages":
        navigate(`/cms/pages/${item.id}`);
        break;
      case "posts":
        navigate(`/cms/posts/${item.id}`);
        break;
      case "categories":
        setEditingCategory(item);
        setShowCategoryModal(true);
        break;
      case "tags":
        setEditingTag(item);
        setShowTagModal(true);
        break;
      case "authors":
        setEditingAuthor(item);
        setShowAuthorModal(true);
        break;
      default:
        break;
    }
  };


  const handleDelete = async (item) => {
    if (needsPayment && showPaymentRequired()) return;

    try {
      switch (activeTab) {
        case "pages":
          await deletePage(item.id);
          toast.success("Page moved to trash");
          break;
        case "posts":
          await deletePost(item.id);
          toast.success("Post moved to trash");
          break;
        default:
          break;
      }
      setShowDeleteConfirm(null);
    } catch (err) {
      toast.error("Failed to delete item");
    }
  };

  const handlePublish = async (item) => {
    if (needsPayment && showPaymentRequired()) return;

    try {
      switch (activeTab) {
        case "pages":
          await publishPage(item.id);
          toast.success("Page published successfully");
          break;
        case "posts":
          await publishPost(item.id);
          toast.success("Post published successfully");
          break;
        default:
          break;
      }
    } catch (err) {
      toast.error("Failed to publish");
    }
  };

  const handleDuplicate = async (item) => {
    if (needsPayment && showPaymentRequired()) return;

    try {
      if (activeTab === "pages") {
        await duplicatePage(item.id);
        toast.success("Page duplicated successfully");
      }
    } catch (err) {
      toast.error("Failed to duplicate");
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      draft: "badge-draft",
      published: "badge-published",
      scheduled: "badge-scheduled",
      pending_review: "badge-pending",
      archived: "badge-archived",
    };

    const statusIcons = {
      draft: <FiEdit3 size={12} />,
      published: <FiCheck size={12} />,
      scheduled: <FiClock size={12} />,
      pending_review: <FiEye size={12} />,
      archived: <FiX size={12} />,
    };

    return (
      <span className={`cms-status-badge ${statusClasses[status] || ""}`}>
        {statusIcons[status]}
        {status?.replace("_", " ")}
      </span>
    );
  };

  const filteredItems = () => {
    let items = [];
    switch (activeTab) {
      case "pages":
        items = pages;
        break;
      case "posts":
        items = posts;
        break;
      case "categories":
        items = categories;
        break;
      case "tags":
        items = tags;
        break;
      case "authors":
        items = authors;
        break;
      default:
        items = [];
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter(item =>
        item.title?.toLowerCase().includes(term) ||
        item.name?.toLowerCase().includes(term) ||
        item.slug?.toLowerCase().includes(term)
      );
    }

    // Apply status filter
    if (statusFilter !== "all" && (activeTab === "pages" || activeTab === "posts")) {
      items = items.filter(item => item.status === statusFilter);
    }

    return items;
  };

  const isLoading = activeTab === "pages" ? pagesLoading :
                    activeTab === "posts" ? postsLoading :
                    activeTab === "categories" ? categoriesLoading :
                    activeTab === "tags" ? tagsLoading :
                    activeTab === "authors" ? authorsLoading : false;

  const tabs = [
    { id: "pages", label: "Pages", icon: <FiFileText />, count: pages.length },
    { id: "posts", label: "Blog Posts", icon: <FiEdit3 />, count: posts.length },
    { id: "categories", label: "Categories", icon: <FiFolder />, count: categories.length },
    { id: "tags", label: "Tags", icon: <FiTag />, count: tags.length },
    { id: "authors", label: "Authors", icon: <FiUsers />, count: authors.length },
    { id: "media", label: "Media", icon: <FiImage />, count: 0 },
    { id: "redirects", label: "Redirects", icon: <FiArrowRight />, count: 0 },
  ];

  return (
    <div className="cms-container">
      {/* Header */}
      <div className="cms-header">
        <div className="cms-header-left">
          <h1 className="cms-title">Content Management</h1>
          <p className="cms-subtitle">Manage your website content, pages, and blog posts</p>
        </div>
        <div className="cms-header-right">
          <button
            className="cms-btn cms-btn-primary"
            onClick={handleCreateNew}
          >
            <FiPlus size={18} />
            <span>Create New</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="cms-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`cms-tab ${activeTab === tab.id ? "active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            <span>{tab.label}</span>
            {tab.count > 0 && <span className="cms-tab-count">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* Filters Bar */}
      <div className="cms-filters">
        <div className="cms-search">
          <FiSearch className="cms-search-icon" />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="cms-search-input"
          />
        </div>

        {(activeTab === "pages" || activeTab === "posts") && (
          <div className="cms-status-filter">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="cms-select"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="scheduled">Scheduled</option>
              <option value="pending_review">Pending Review</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        )}

        <button
          className="cms-btn cms-btn-outline"
          onClick={() => {
            if (activeTab === "pages") fetchPages();
            else if (activeTab === "posts") fetchPosts();
            else if (activeTab === "categories") fetchCategories();
            else if (activeTab === "tags") fetchTags();
            else if (activeTab === "authors") fetchAuthors();
          }}
        >
          <FiRefreshCw size={16} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Content List */}
      <div className="cms-content">
        {isLoading ? (
          <div className="cms-loading">
            <FullScreenLoader />
          </div>
        ) : filteredItems().length === 0 ? (
          <div className="cms-empty">
            <div className="cms-empty-icon">
              {activeTab === "pages" ? <FiFileText size={48} /> :
               activeTab === "posts" ? <FiEdit3 size={48} /> :
               activeTab === "categories" ? <FiFolder size={48} /> :
               activeTab === "tags" ? <FiTag size={48} /> :
               activeTab === "authors" ? <FiUsers size={48} /> :
               activeTab === "media" ? <FiImage size={48} /> :
               <FiArrowRight size={48} />}
            </div>
            <h3>No {activeTab} found</h3>
            <p>Create your first {activeTab.slice(0, -1)} to get started</p>
            <button className="cms-btn cms-btn-primary" onClick={handleCreateNew}>
              <FiPlus size={18} />
              <span>Create {activeTab === "pages" ? "Page" : activeTab === "posts" ? "Post" : activeTab.slice(0, -1)}</span>
            </button>
          </div>
        ) : (
          <div className="cms-table-container">
            <table className="cms-table">
              <thead>
                <tr>
                  <th>Title</th>
                  {(activeTab === "pages" || activeTab === "posts") && <th>Status</th>}
                  {activeTab === "posts" && <th>Category</th>}
                  {activeTab === "posts" && <th>Author</th>}
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems().map(item => (
                  <tr key={item.id}>
                    <td>
                      <div className="cms-item-title">
                        <span className="cms-item-name">{item.title || item.name}</span>
                        <span className="cms-item-slug">/{item.slug}</span>
                      </div>
                    </td>
                    {(activeTab === "pages" || activeTab === "posts") && (
                      <td>{getStatusBadge(item.status)}</td>
                    )}
                    {activeTab === "posts" && (
                      <td>
                        {item.category ? (
                          <span className="cms-category-badge" style={{ backgroundColor: item.category.color || '#6B7280' }}>
                            {item.category.name}
                          </span>
                        ) : (
                          <span className="cms-no-category">No category</span>
                        )}
                      </td>
                    )}
                    {activeTab === "posts" && (
                      <td>
                        {item.author ? (
                          <span className="cms-author-name">{item.author.name}</span>
                        ) : (
                          <span className="cms-no-author">No author</span>
                        )}
                      </td>
                    )}
                    <td>
                      <span className="cms-date">
                        {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : "-"}
                      </span>
                    </td>
                    <td>
                      <div className="cms-actions">
                        <button
                          className="cms-action-btn"
                          onClick={() => handleEditItem(item)}
                          title="Edit"
                        >
                          <FiEdit size={16} />
                        </button>
                        {item.status !== "published" && (
                          <button
                            className="cms-action-btn cms-action-publish"
                            onClick={() => handlePublish(item)}
                            title="Publish"
                          >
                            <FiCheck size={16} />
                          </button>
                        )}
                        {activeTab === "pages" && (
                          <button
                            className="cms-action-btn"
                            onClick={() => handleDuplicate(item)}
                            title="Duplicate"
                          >
                            <FiCopy size={16} />
                          </button>
                        )}
                        {item.status === "published" && (
                          <button
                            className="cms-action-btn"
                            onClick={() => window.open(`/${activeTab === "pages" ? "pages" : "blog"}/${item.slug}`, "_blank")}
                            title="View"
                          >
                            <FiExternalLink size={16} />
                          </button>
                        )}
                        <button
                          className="cms-action-btn cms-action-delete"
                          onClick={() => setShowDeleteConfirm(item)}
                          title="Delete"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="cms-modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="cms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cms-modal-header">
              <h3>Delete {activeTab.slice(0, -1)}?</h3>
              <button className="cms-modal-close" onClick={() => setShowDeleteConfirm(null)}>
                <FiX size={20} />
              </button>
            </div>
            <div className="cms-modal-body">
              <p>Are you sure you want to delete "{showDeleteConfirm.title || showDeleteConfirm.name}"?</p>
              <p className="cms-modal-warning">This action can be undone from the trash.</p>
            </div>
            <div className="cms-modal-footer">
              <button
                className="cms-btn cms-btn-outline"
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="cms-btn cms-btn-danger"
                onClick={() => handleDelete(showDeleteConfirm)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      <CategoryModal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setEditingCategory(null);
        }}
        category={editingCategory}
      />

      {/* Tag Modal */}
      <TagModal
        isOpen={showTagModal}
        onClose={() => {
          setShowTagModal(false);
          setEditingTag(null);
        }}
        tag={editingTag}
      />

      {/* Author Modal */}
      <AuthorModal
        isOpen={showAuthorModal}
        onClose={() => {
          setShowAuthorModal(false);
          setEditingAuthor(null);
        }}
        author={editingAuthor}
      />

      {/* Payment Block Modal */}
      {showBlockModal && (
        <PaymentBlockModal onClose={closeBlockModal} />
      )}
    </div>
  );
};

export default CMS;
